import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Modal, SUB_CONFIRM_LAYER, TextButton, useConfirmDialog } from '@safetyhub/ui';
import { adminApi, extractApiError } from '../lib/api';

// 타입 정의
/**
 * 서버 Prisma `ScheduledTaskType` 전수 — `INSPECTION_PURGE`는 2026-10-07에 **폐지**됐지만 enum 값과
 * 기존 행(취소됨 · `INSPECTION_PURGE_RETIRED`)이 남아 이 목록에 내려온다. 빠뜨리면 그 행의 타입 칸이
 * 빈칸이었다(리뷰 1103 F10).
 */
type TaskType = 'USER_PURGE' | 'ORGANIZATION_PURGE' | 'EXPORT_CLEANUP' | 'INSPECTION_PURGE';
type TaskStatus = 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';

interface Task {
  id: string;
  type: TaskType;
  targetId: string;
  targetType: string;
  targetInfo: { name: string; detail?: string } | null;
  scheduledAt: string;
  status: TaskStatus;
  executedAt: string | null;
  result: string | null;
  retryCount: number;
  metadata: Record<string, unknown> | null;
  cancelledAt: string | null;
  cancelReason: string | null;
  createdAt: string;
  updatedAt: string;
  organizationsToDelete?: Array<{
    id: string;
    name: string;
    category: string;
    memberCount: number;
  }>;
  relatedOrgPurgeTasks?: Array<{
    id: string;
    organizationId: string;
    organizationName: string;
    status: string;
  }>;
  // ORGANIZATION_PURGE 태스크 전용: 복원 가능 여부
  canRestore?: boolean;
  cannotRestoreReason?: string;
}

// 한글 매핑
const TASK_TYPE_LABELS: Record<TaskType, string> = {
  USER_PURGE: '사용자 삭제',
  ORGANIZATION_PURGE: '조직 삭제',
  EXPORT_CLEANUP: '내보내기 정리',
  INSPECTION_PURGE: '점검 삭제(폐지)',
};

const TASK_STATUS_LABELS: Record<TaskStatus, string> = {
  PENDING: '대기',
  PROCESSING: '처리중',
  COMPLETED: '완료',
  FAILED: '실패',
  CANCELLED: '취소됨',
};

const TASK_STATUS_COLORS: Record<TaskStatus, string> = {
  PENDING: 'bg-gray-100 text-gray-800',
  PROCESSING: 'bg-blue-100 text-blue-800',
  COMPLETED: 'bg-green-100 text-green-800',
  FAILED: 'bg-red-100 text-red-800',
  CANCELLED: 'bg-yellow-100 text-yellow-800',
};

const TARGET_TYPE_LABELS: Record<string, string> = {
  user: '사용자',
  organization: '조직',
  data_export: '내보내기',
  // 폐지된 INSPECTION_PURGE 행의 대상 타입(구 `scheduleInspectionPurge`가 쓰던 값)
  inspection: '점검',
};

export default function Tasks() {
  const queryClient = useQueryClient();
  const [confirm, confirmDialog] = useConfirmDialog();
  const [statusFilter, setStatusFilter] = useState<TaskStatus | ''>('');
  const [typeFilter, setTypeFilter] = useState<TaskType | ''>('');
  const [selectedTask, setSelectedTask] = useState<string | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailTask, setDetailTask] = useState<Task | null>(null);

  // 태스크 목록 조회
  const { data: tasksData, isLoading: tasksLoading } = useQuery({
    queryKey: ['admin', 'tasks', statusFilter, typeFilter],
    queryFn: () =>
      adminApi.getTasks({
        limit: 100,
        status: statusFilter || undefined,
        type: typeFilter || undefined,
      }),
  });

  // 통계 조회
  const { data: statsData } = useQuery({
    queryKey: ['admin', 'tasks', 'stats'],
    queryFn: () => adminApi.getTaskStats(),
  });

  // 태스크 취소
  const cancelMutation = useMutation({
    mutationFn: ({ taskId, reason }: { taskId: string; reason?: string }) =>
      adminApi.cancelTask(taskId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'tasks'] });
      setShowCancelModal(false);
      setCancelReason('');
      setSelectedTask(null);
    },
  });

  // 태스크 재시도
  const retryMutation = useMutation({
    mutationFn: (taskId: string) => adminApi.retryTask(taskId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'tasks'] });
    },
  });

  // 태스크 즉시 실행
  const executeMutation = useMutation({
    mutationFn: (taskId: string) => adminApi.executeTask(taskId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'tasks'] });
      setShowDetailModal(false);
      setDetailTask(null);
      alert(data.data?.status === 'COMPLETED' ? '태스크가 성공적으로 실행되었습니다.' : '태스크 실행 중 오류가 발생했습니다.');
    },
    onError: (error) => {
      alert(extractApiError(error).message);
    },
  });

  const handleCancel = (taskId: string) => {
    setSelectedTask(taskId);
    setShowCancelModal(true);
  };

  const confirmCancel = () => {
    if (selectedTask) {
      cancelMutation.mutate({ taskId: selectedTask, reason: cancelReason || undefined });
    }
  };

  // 재시도·즉시 실행은 목록 행과 상세 모달 양쪽에서 부른다 — 상세(z-[70])에서 부를 때는 그 위 층
  // (`SUB_CONFIRM_LAYER`)을 넘긴다. 층은 여는 쪽이 정한다.
  const handleRetry = async (taskId: string, layer?: typeof SUB_CONFIRM_LAYER) => {
    if (
      await confirm({
        title: '태스크 재시도',
        body: '이 태스크를 재시도하시겠습니까?',
        confirmLabel: '재시도',
        ...layer,
      })
    ) {
      retryMutation.mutate(taskId);
    }
  };

  const handleExecute = async (taskId: string, task?: Task, layer?: typeof SUB_CONFIRM_LAYER) => {
    // USER_PURGE 태스크이고 연관 조직 삭제 태스크가 있는 경우 추가 경고
    if (task?.type === 'USER_PURGE' && task.relatedOrgPurgeTasks && task.relatedOrgPurgeTasks.length > 0) {
      const orgNames = task.relatedOrgPurgeTasks.map(t => t.organizationName).join(', ');
      const confirmed = await confirm({
        title: '즉시 실행 — 연관 조직 삭제 주의',
        body:
          `주의: 이 사용자에게 연관된 조직 삭제 태스크가 ${task.relatedOrgPurgeTasks.length}개 있습니다.\n\n` +
          `조직: ${orgNames}\n\n` +
          `사용자 삭제 실행 후에는 해당 조직들의 복원이 불가능해집니다.\n` +
          `정말 즉시 실행하시겠습니까?`,
        confirmLabel: '즉시 실행',
        danger: true,
        ...layer,
      });
      if (confirmed) {
        executeMutation.mutate(taskId);
      }
    } else if (
      await confirm({
        title: '즉시 실행',
        body: '이 태스크를 지금 즉시 실행하시겠습니까? 예정 시간과 관계없이 바로 실행됩니다.',
        confirmLabel: '즉시 실행',
        // 파기·삭제 태스크가 섞여 있어 되돌릴 수 없는 실행일 수 있다 — Enter가 취소가 되게 둔다.
        danger: true,
        ...layer,
      })
    ) {
      executeMutation.mutate(taskId);
    }
  };

  const handleRowClick = (task: Task) => {
    setDetailTask(task);
    setShowDetailModal(true);
  };

  if (tasksLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">로딩 중...</div>
      </div>
    );
  }

  const tasks = (tasksData?.data || []) as Task[];
  const stats = statsData?.data;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">예약 작업</h2>
      </div>

      {/* 통계 카드 */}
      {stats && (
        <div className="grid grid-cols-5 gap-4 mb-6">
          <StatCard label="대기" value={stats.pending} color="gray" />
          <StatCard label="처리중" value={stats.processing} color="blue" />
          <StatCard label="완료" value={stats.completed} color="green" />
          <StatCard label="실패" value={stats.failed} color="red" />
          <StatCard label="취소됨" value={stats.cancelled} color="yellow" />
        </div>
      )}

      {/* 필터 */}
      <div className="flex gap-4 mb-4">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as TaskStatus | '')}
          className="px-3 py-2 border rounded-control text-sm"
        >
          <option value="">모든 상태</option>
          <option value="PENDING">대기</option>
          <option value="PROCESSING">처리중</option>
          <option value="COMPLETED">완료</option>
          <option value="FAILED">실패</option>
          <option value="CANCELLED">취소됨</option>
        </select>

        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value as TaskType | '')}
          className="px-3 py-2 border rounded-control text-sm"
        >
          <option value="">모든 타입</option>
          {/* 라벨 표에서 파생한다 — 손열거면 타입을 더할 때 필터에서만 빠진다(폐지 타입도 거를 수 있어야 한다) */}
          {(Object.keys(TASK_TYPE_LABELS) as TaskType[]).map((type) => (
            <option key={type} value={type}>
              {TASK_TYPE_LABELS[type]}
            </option>
          ))}
        </select>
      </div>

      {/* 태스크 테이블 */}
      <Card padding="none" radius="lg" className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                타입
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                대상
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                생성일시
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                예정일시
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                상태
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                재시도
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                액션
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {tasks.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                  예약된 작업이 없습니다.
                </td>
              </tr>
            ) : (
              tasks.map((task) => (
                <tr
                  key={task.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => handleRowClick(task)}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {TASK_TYPE_LABELS[task.type]}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {task.targetInfo ? (
                      <>
                        <div className="text-sm font-medium text-gray-900">
                          {task.targetInfo.name}
                        </div>
                        {task.targetInfo.detail && (
                          <div className="text-xs text-gray-500">{task.targetInfo.detail}</div>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="text-sm text-gray-500">
                          {TARGET_TYPE_LABELS[task.targetType] || task.targetType}
                        </div>
                        <div className="text-xs text-gray-400 font-mono">
                          {task.targetId.slice(0, 8)}...
                        </div>
                      </>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(task.createdAt).toLocaleString('ko-KR')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(task.scheduledAt).toLocaleString('ko-KR')}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="relative inline-block group">
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${TASK_STATUS_COLORS[task.status]} ${
                          (task.status === 'FAILED' && task.result) || (task.status === 'CANCELLED' && task.cancelReason) ? 'cursor-help' : ''
                        }`}
                      >
                        {TASK_STATUS_LABELS[task.status]}
                      </span>
                      {/* 실패 시 에러 메시지 툴팁 */}
                      {task.result && task.status === 'FAILED' && (
                        <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block z-50">
                          <div className="bg-gray-900 text-white text-xs rounded-card-sm px-3 py-2 max-w-xs shadow-lg">
                            <div className="font-medium mb-1">에러 메시지:</div>
                            <div className="whitespace-pre-wrap break-words max-h-40 overflow-y-auto">
                              {task.result}
                            </div>
                          </div>
                          <div className="absolute left-4 top-full border-4 border-transparent border-t-gray-900" />
                        </div>
                      )}
                      {/* 취소 사유 툴팁 */}
                      {task.cancelReason && task.status === 'CANCELLED' && (
                        <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block z-50">
                          <div className="bg-gray-900 text-white text-xs rounded-card-sm px-3 py-2 max-w-xs shadow-lg">
                            <div className="font-medium mb-1">취소 사유:</div>
                            <div className="whitespace-pre-wrap break-words max-h-40 overflow-y-auto">
                              {task.cancelReason}
                            </div>
                          </div>
                          <div className="absolute left-4 top-full border-4 border-transparent border-t-gray-900" />
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {task.retryCount}회
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                      {task.status === 'PENDING' && (
                        <>
                          <button
                            onClick={() => handleExecute(task.id, task)}
                            className="text-green-600 hover:text-green-800"
                            disabled={executeMutation.isPending}
                          >
                            실행
                          </button>
                          <button
                            onClick={() => handleCancel(task.id)}
                            className={`${
                              task.canRestore === false
                                ? 'text-gray-400 cursor-not-allowed'
                                : 'text-yellow-600 hover:text-yellow-800'
                            }`}
                            disabled={cancelMutation.isPending || task.canRestore === false}
                            title={task.cannotRestoreReason || undefined}
                          >
                            취소
                          </button>
                        </>
                      )}
                      {task.status === 'FAILED' && task.retryCount < 3 && (
                        <TextButton
                          onClick={() => handleRetry(task.id)}
                          disabled={retryMutation.isPending}
                        >
                          재시도
                        </TextButton>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Card>

      {/* 취소 확인 모달 */}
      {showCancelModal && (
        <Modal
          open
          onClose={() => {
            setShowCancelModal(false);
            setCancelReason('');
            setSelectedTask(null);
          }}
          title="태스크 취소"
          size="md"
          footer={
            <>
              <button
                onClick={() => {
                  setShowCancelModal(false);
                  setCancelReason('');
                  setSelectedTask(null);
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                닫기
              </button>
              <Button
                variant="warning"
                onClick={confirmCancel}
                disabled={cancelMutation.isPending}
              >
                {cancelMutation.isPending ? '처리 중...' : '취소하기'}
              </Button>
            </>
          }
        >
            <p className="text-sm text-gray-600 mb-4">
              이 태스크를 취소하시겠습니까? 취소 사유를 입력해주세요.
            </p>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="취소 사유 (선택)"
              className="w-full px-3 py-2 border rounded-control"
              rows={3}
            />
            {cancelMutation.error && (
              <p className="text-sm text-red-600 mt-4">
                {extractApiError(cancelMutation.error).message}
              </p>
            )}
        </Modal>
      )}

      {/* 상세 보기 모달 */}
      {showDetailModal && detailTask && (
        <TaskDetailModal
          task={detailTask}
          onClose={() => {
            setShowDetailModal(false);
            setDetailTask(null);
          }}
          onExecute={() => handleExecute(detailTask.id, detailTask, SUB_CONFIRM_LAYER)}
          onCancel={() => {
            setShowDetailModal(false);
            handleCancel(detailTask.id);
          }}
          onRetry={() => handleRetry(detailTask.id, SUB_CONFIRM_LAYER)}
          isExecuting={executeMutation.isPending}
        />
      )}
      {confirmDialog}
    </div>
  );
}

// 상세 보기 모달 컴포넌트
function TaskDetailModal({
  task,
  onClose,
  onExecute,
  onCancel,
  onRetry,
  isExecuting,
}: {
  task: Task;
  onClose: () => void;
  onExecute: () => void;
  onCancel: () => void;
  onRetry: () => void;
  isExecuting: boolean;
}) {
  return (
    // 부모가 열 때만 마운트하므로 open은 늘 true다. 종전 패널 스크롤(max-h-[90vh])은 base 본문 스크롤이 맡는다
    // — 헤더·액션 줄이 고정되고 본문만 스크롤된다.
    <Modal
      open
      onClose={onClose}
      title="태스크 상세 정보"
      size="xl"
      footer={
        <>
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            닫기
          </button>
          {task.status === 'PENDING' && (
            <>
              <div className="relative group">
                <button
                  onClick={onCancel}
                  disabled={task.canRestore === false}
                  className={`px-4 py-2 rounded-control ${
                    task.canRestore === false
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                  }`}
                >
                  취소
                </button>
                {/* 버튼 오른쪽 끝에 맞춰 왼쪽으로 펼친다 — 가운데 정렬이면 긴 사유(탈퇴 대기)가 패널 overflow-hidden에
                    오른쪽 48px 잘렸다(2026-10-30 dev 실측). 오른쪽엔 「즉시 실행」 버튼이 있어 이 기준이 패널 안에 든다. */}
                {task.canRestore === false && task.cannotRestoreReason && (
                  <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-card-sm opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                    {task.cannotRestoreReason}
                    <div className="absolute top-full right-6 border-4 border-transparent border-t-gray-800" />
                  </div>
                )}
              </div>
              <Button variant="success" onClick={onExecute} disabled={isExecuting}>
                {isExecuting ? '실행 중...' : '즉시 실행'}
              </Button>
            </>
          )}
          {task.status === 'FAILED' && task.retryCount < 3 && (
            <Button variant="primary" onClick={onRetry}>
              재시도
            </Button>
          )}
        </>
      }
    >
        {/* 기본 정보 */}
        <div className="space-y-4 mb-6">
          <div className="grid grid-cols-2 gap-4">
            <InfoRow label="태스크 ID" value={task.id} mono />
            <InfoRow label="타입" value={TASK_TYPE_LABELS[task.type]} />
            <InfoRow label="상태">
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${TASK_STATUS_COLORS[task.status]}`}>
                {TASK_STATUS_LABELS[task.status]}
              </span>
            </InfoRow>
            <InfoRow label="재시도 횟수" value={`${task.retryCount}회`} />
          </div>

          <div className="border-t pt-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">일시</h4>
            <div className="grid grid-cols-2 gap-4">
              <InfoRow label="예정 일시" value={new Date(task.scheduledAt).toLocaleString('ko-KR')} />
              <InfoRow label="생성 일시" value={new Date(task.createdAt).toLocaleString('ko-KR')} />
              {task.executedAt && (
                <InfoRow label="실행 일시" value={new Date(task.executedAt).toLocaleString('ko-KR')} />
              )}
              {task.cancelledAt && (
                <InfoRow label="취소 일시" value={new Date(task.cancelledAt).toLocaleString('ko-KR')} />
              )}
            </div>
          </div>
        </div>

        {/* 대상 정보 */}
        <div className="border-t pt-4 mb-6">
          <h4 className="text-sm font-medium text-gray-700 mb-3">대상 정보</h4>
          <div className="bg-gray-50 rounded-card p-4">
            <div className="grid grid-cols-2 gap-4">
              <InfoRow label="대상 타입" value={TARGET_TYPE_LABELS[task.targetType] || task.targetType} />
              <InfoRow label="대상 ID" value={task.targetId} mono />
              {task.targetInfo && (
                <>
                  <InfoRow label="대상 이름" value={task.targetInfo.name} />
                  {task.targetInfo.detail && (
                    <InfoRow label="상세" value={task.targetInfo.detail} />
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* 타입별 상세 정보 */}
        <TaskTypeSpecificInfo task={task} />

        {/* 결과 정보 */}
        {(task.result || task.cancelReason) && (
          <div className="border-t pt-4 mb-6">
            <h4 className="text-sm font-medium text-gray-700 mb-3">결과</h4>
            {task.cancelReason && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-card p-3 mb-2">
                <div className="text-sm text-yellow-800">
                  <span className="font-medium">취소 사유:</span> {task.cancelReason}
                </div>
              </div>
            )}
            {task.result && (
              <div className={`rounded-card p-3 ${task.status === 'FAILED' ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`}>
                <div className={`text-sm ${task.status === 'FAILED' ? 'text-red-800' : 'text-green-800'}`}>
                  <span className="font-medium">실행 결과:</span>
                  <pre className="mt-1 whitespace-pre-wrap font-mono text-xs">{task.result}</pre>
                </div>
              </div>
            )}
          </div>
        )}
    </Modal>
  );
}

// 타입별 상세 정보 컴포넌트
function TaskTypeSpecificInfo({ task }: { task: Task }) {
  const metadata = task.metadata || {};

  // 안전하게 문자열 값 가져오기
  const getString = (key: string): string | undefined => {
    const val = metadata[key];
    return val !== undefined && val !== null ? String(val) : undefined;
  };

  const getNumber = (key: string): number | undefined => {
    const val = metadata[key];
    return typeof val === 'number' ? val : undefined;
  };

  if (task.type === 'USER_PURGE') {
    const reason = getString('reason');
    const deletionSource = getString('deletionSource');
    const email = getString('email');
    const phone = getString('phone');
    const orgsToDelete = task.organizationsToDelete || [];
    const relatedOrgTasks = task.relatedOrgPurgeTasks || [];

    return (
      <div className="border-t pt-4 mb-6">
        <h4 className="text-sm font-medium text-gray-700 mb-3">사용자 삭제 상세</h4>
        <div className="bg-brand-50 border border-brand-200 rounded-card p-4">
          <div className="grid grid-cols-2 gap-4">
            {reason && (
              <InfoRow label="삭제 사유" value={reason} fullWidth />
            )}
            {deletionSource && (
              <InfoRow
                label="삭제 요청자"
                value={deletionSource === 'SELF' ? '본인 탈퇴' : deletionSource === 'ADMIN' ? '관리자' : deletionSource}
              />
            )}
            {email && (
              <InfoRow label="이메일" value={email} />
            )}
            {phone && (
              <InfoRow label="전화번호" value={phone} />
            )}
          </div>

          {/* 함께 삭제될 조직 목록 */}
          {orgsToDelete.length > 0 && (
            <div className="mt-4 pt-3 border-t border-brand-200">
              <h5 className="text-xs font-medium text-brand-800 mb-2">
                함께 삭제될 조직 ({orgsToDelete.length}개)
              </h5>
              <div className="space-y-2">
                {orgsToDelete.map((org) => (
                  <div
                    key={org.id}
                    className="flex items-center justify-between bg-white rounded px-3 py-2 text-sm"
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{org.name}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                        org.category === 'WORKSPACE'
                          ? 'bg-gray-100 text-gray-600'
                          : 'bg-brand-100 text-brand-600'
                      }`}>
                        {org.category === 'WORKSPACE' ? '개인/팀' : '비즈니스'}
                      </span>
                    </div>
                    <span className="text-xs text-gray-500">
                      멤버 {org.memberCount}명
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 연관 조직 삭제 태스크 (별도 삭제 요청된 조직) */}
          {relatedOrgTasks.length > 0 && (
            <div className="mt-4 pt-3 border-t border-brand-200">
              <h5 className="text-xs font-medium text-amber-700 mb-2 flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                별도 삭제 예정 조직 ({relatedOrgTasks.length}개)
              </h5>
              <div className="bg-amber-50 border border-amber-200 rounded-card p-3 mb-2">
                <p className="text-xs text-amber-800 mb-2">
                  아래 조직들은 별도의 조직 삭제 태스크가 등록되어 있습니다.
                  사용자 삭제 태스크 실행 후 해당 조직 태스크를 취소(복원)하면 원래 소유자가 없어 복원이 불가능합니다.
                </p>
                <div className="space-y-1">
                  {relatedOrgTasks.map((orgTask) => (
                    <div
                      key={orgTask.id}
                      className="flex items-center justify-between bg-white rounded px-3 py-2 text-sm"
                    >
                      <span className="font-medium text-gray-900">{orgTask.organizationName}</span>
                      <span className="text-xs text-gray-500 font-mono">
                        {orgTask.id.slice(0, 8)}...
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <p className="text-xs text-brand-700 mt-3">
            이 작업은 사용자 계정을 영구적으로 삭제하고 관련 데이터를 정리합니다.
          </p>
        </div>
      </div>
    );
  }

  if (task.type === 'ORGANIZATION_PURGE') {
    const reason = getString('reason');
    const organizationType = getString('organizationType');
    const memberCount = getNumber('memberCount');
    const siteCount = getNumber('siteCount');
    const inspectionCount = getNumber('inspectionCount');

    return (
      <div className="border-t pt-4 mb-6">
        <h4 className="text-sm font-medium text-gray-700 mb-3">조직 삭제 상세</h4>
        <div className="bg-purple-50 rounded-card p-4">
          <div className="grid grid-cols-2 gap-4">
            {reason && (
              <InfoRow label="삭제 사유" value={reason} fullWidth />
            )}
            {organizationType && (
              <InfoRow
                label="조직 타입"
                value={organizationType === 'PERSONAL' ? '개인' : organizationType === 'GROUP' ? '비즈니스' : organizationType}
              />
            )}
            {memberCount !== undefined && (
              <InfoRow label="멤버 수" value={`${memberCount}명`} />
            )}
            {siteCount !== undefined && (
              <InfoRow label="현장 수" value={`${siteCount}개`} />
            )}
            {inspectionCount !== undefined && (
              <InfoRow label="점검 수" value={`${inspectionCount}건`} />
            )}
          </div>

          {/* 복원 불가 경고 */}
          {task.canRestore === false && task.cannotRestoreReason && (
            <div className="mt-4 pt-3 border-t border-purple-200">
              <div className="bg-red-50 border border-red-200 rounded-card p-3 flex items-start gap-2">
                <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div>
                  <p className="text-sm font-medium text-red-800">복원 불가</p>
                  <p className="text-xs text-red-700 mt-1">{task.cannotRestoreReason}</p>
                </div>
              </div>
            </div>
          )}

          <p className="text-xs text-purple-600 mt-3">
            이 작업은 조직과 모든 관련 데이터(현장, 점검, 보고서 등)를 영구적으로 삭제합니다.
          </p>
        </div>
      </div>
    );
  }

  if (task.type === 'EXPORT_CLEANUP') {
    const exportType = getString('exportType');
    const fileSize = getNumber('fileSize');
    const createdAt = getString('createdAt');
    const downloadCount = getNumber('downloadCount');

    return (
      <div className="border-t pt-4 mb-6">
        <h4 className="text-sm font-medium text-gray-700 mb-3">내보내기 정리 상세</h4>
        <div className="bg-orange-50 rounded-card p-4">
          <div className="grid grid-cols-2 gap-4">
            {exportType && (
              <InfoRow label="내보내기 타입" value={exportType} />
            )}
            {fileSize !== undefined && (
              <InfoRow label="파일 크기" value={formatFileSize(fileSize)} />
            )}
            {createdAt && (
              <InfoRow label="생성 일시" value={new Date(createdAt).toLocaleString('ko-KR')} />
            )}
            {downloadCount !== undefined && (
              <InfoRow label="다운로드 횟수" value={`${downloadCount}회`} />
            )}
          </div>
          <p className="text-xs text-orange-600 mt-3">
            이 작업은 만료된 내보내기 파일을 삭제하여 저장 공간을 정리합니다.
          </p>
        </div>
      </div>
    );
  }

  // 기타 타입 또는 메타데이터가 있는 경우
  if (Object.keys(metadata).length > 0) {
    return (
      <div className="border-t pt-4 mb-6">
        <h4 className="text-sm font-medium text-gray-700 mb-3">추가 정보</h4>
        <div className="bg-gray-50 rounded-card p-4">
          <pre className="text-xs font-mono whitespace-pre-wrap">
            {JSON.stringify(metadata, null, 2)}
          </pre>
        </div>
      </div>
    );
  }

  return null;
}

// 정보 행 컴포넌트
function InfoRow({
  label,
  value,
  children,
  mono,
  fullWidth,
}: {
  label: string;
  value?: string;
  children?: React.ReactNode;
  mono?: boolean;
  fullWidth?: boolean;
}) {
  return (
    <div className={fullWidth ? 'col-span-2' : ''}>
      <dt className="text-xs text-gray-500">{label}</dt>
      <dd className={`text-sm text-gray-900 mt-0.5 ${mono ? 'font-mono' : ''}`}>
        {children || value || '-'}
      </dd>
    </div>
  );
}

// 파일 크기 포맷
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

// 통계 카드 컴포넌트
function StatCard({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color: 'gray' | 'blue' | 'green' | 'red' | 'yellow';
}) {
  const colorClasses = {
    gray: 'bg-gray-50 border-gray-200 text-gray-600',
    blue: 'bg-blue-50 border-blue-200 text-blue-600',
    green: 'bg-green-50 border-green-200 text-green-600',
    red: 'bg-red-50 border-red-200 text-red-600',
    yellow: 'bg-yellow-50 border-yellow-200 text-yellow-600',
  };

  return (
    <div className={`rounded-card border p-4 ${colorClasses[color]}`}>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-sm">{label}</div>
    </div>
  );
}
